import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { parseReward } from 'state/global-state/models/loyalty/offers/offers.utils';
import { IAppliedRewards, ICartEntryAdapter } from 'state/loyalty/hooks/types';
import { parseEntry } from 'state/loyalty/hooks/utils/incentive-evaluation';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';

import { EvaluateLoyaltyServiceRewardsParams } from '../hooks/use-evaluate-loyalty-service-rewards';

interface BuildAppliedRewardServiceInputsParams {
  /** Rewards persisted as applied to the cart (Redux `appliedLoyaltyRewards`). */
  appliedRewards: IAppliedRewards | null;
  /** Legacy cart entries to validate the applied rewards against. */
  cartEntries?: ILegacyCartEntry[];
  /**
   * Applied offers, used to drop cart lines already claimed by an offer so they
   * aren't double-counted against reward requirements.
   */
  appliedOffers?: LoyaltyAppliedOffer[];
}

/**
 * Shapes persisted applied rewards + legacy cart entries into the
 * `appliedIncentives` / `cartEntries` inputs `evaluateLoyaltyServiceRewards`
 * expects.
 *
 * Mirrors the reshaping the legacy `evaluateLoyaltyUserRewards` performs
 * internally (`parseReward` per applied cartId + `parseEntry` REWARD, excluding
 * offer-covered lines), so the ON `your-cart` seam feeds the same Redux
 * `rewardsFeedbackMap` the cart already reads.
 */
export const buildAppliedRewardServiceInputs = ({
  appliedRewards,
  cartEntries,
  appliedOffers,
}: BuildAppliedRewardServiceInputsParams): Pick<
  EvaluateLoyaltyServiceRewardsParams,
  'appliedIncentives' | 'cartEntries'
> => {
  const rewards = appliedRewards ?? {};
  const appliedIncentives = Object.keys(rewards).map(cartId =>
    parseReward(rewards[cartId], cartId)
  );
  const parsedEntries = (cartEntries ?? []).reduce(
    parseEntry({
      type: LoyaltyPromotionType.REWARD,
      incentivesToExclude: appliedOffers,
    }),
    [] as ICartEntryAdapter[]
  );

  return { appliedIncentives, cartEntries: parsedEntries };
};
